import type { DbSafetyInfo } from "@/services/health/DbSafetyService";
import type { StripeSafetyInfo } from "@/services/health/EnvironmentSafetyService";
import type { AdminDeployEnvironment } from "@/utils/deployEnvironment";

export type BadgeSeverity = "error" | "warning" | "success" | "info";
export type BadgeColor = "default" | "success" | "warning" | "error" | "info";

export interface EnvironmentSafetyBadgeDescriptor {
  id:
    | "environment"
    | "stripe-mode"
    | "db-safety"
    | "email-override"
    | "api-origin";
  severity: BadgeSeverity;
  text: string;
}

interface BuildEnvironmentSafetyBadgesArgs {
  deployEnvironment: AdminDeployEnvironment;
  dbSafety: DbSafetyInfo | null;
  stripeSafety: StripeSafetyInfo | null;
  overrideEmail: string | null;
  apiOrigin: string;
}

const toUppercaseEnvironmentLabel = (
  deployEnvironment: AdminDeployEnvironment,
): string => {
  if (deployEnvironment === "unknown") {
    return "UNKNOWN";
  }

  return deployEnvironment.toUpperCase();
};

const buildDbSafetyDescriptor = (
  dbSafety: DbSafetyInfo | null,
): EnvironmentSafetyBadgeDescriptor => {
  if (!dbSafety) {
    return {
      id: "db-safety",
      severity: "warning",
      text: "DB SAFETY UNKNOWN — TREAT AS PRODUCTION",
    };
  }

  if (dbSafety.isProduction) {
    return {
      id: "db-safety",
      severity: "error",
      text: "PRODUCTION DATABASE SELECTED — REAL CUSTOMER DATA",
    };
  }

  const host = dbSafety.dbHost ?? "unknown-host";
  const name = dbSafety.dbName ?? "unknown-db";
  return {
    id: "db-safety",
    severity: "success",
    text: `DEVELOPMENT DATABASE SELECTED (HOST: ${host}, DB: ${name})`,
  };
};

const buildStripeSafetyDescriptor = (
  stripeSafety: StripeSafetyInfo | null,
): EnvironmentSafetyBadgeDescriptor => {
  if (!stripeSafety) {
    return {
      id: "stripe-mode",
      severity: "warning",
      text: "STRIPE MODE UNKNOWN",
    };
  }

  const runtimeLabel = stripeSafety.runtimeMode.toUpperCase();
  const configuredLabel = stripeSafety.configuredMode.toUpperCase();

  if (stripeSafety.runtimeMode === "unknown") {
    return {
      id: "stripe-mode",
      severity: "warning",
      text: `STRIPE MODE: UNKNOWN (CONFIG ${configuredLabel})`,
    };
  }

  if (stripeSafety.modeMatchesConfiguration === false) {
    return {
      id: "stripe-mode",
      severity: "error",
      text: `STRIPE MISMATCH: API ${runtimeLabel}, CONFIG ${configuredLabel}`,
    };
  }

  if (stripeSafety.runtimeMode === "live") {
    return {
      id: "stripe-mode",
      severity: "error",
      text: "STRIPE MODE: LIVE",
    };
  }

  return {
    id: "stripe-mode",
    severity: "success",
    text: "STRIPE MODE: TEST",
  };
};

export const buildEnvironmentSafetyBadges = ({
  deployEnvironment,
  dbSafety,
  stripeSafety,
  overrideEmail,
  apiOrigin,
}: BuildEnvironmentSafetyBadgesArgs): EnvironmentSafetyBadgeDescriptor[] => {
  const normalizedApiOrigin = apiOrigin.trim();

  return [
    {
      id: "environment",
      severity: "info",
      text: `ENVIRONMENT: ${toUppercaseEnvironmentLabel(deployEnvironment)}`,
    },
    buildStripeSafetyDescriptor(stripeSafety),
    {
      id: "email-override",
      severity: overrideEmail ? "success" : "warning",
      text: overrideEmail
        ? `EMAIL OVERRIDE: ${overrideEmail}`
        : "EMAIL OVERRIDE NOT SET",
    },
    buildDbSafetyDescriptor(dbSafety),
    {
      id: "api-origin",
      severity: "info",
      text: `API ORIGIN: ${normalizedApiOrigin || "NOT CONFIGURED"}`,
    },
  ];
};

export const summarizeBadgeColor = (
  badges: EnvironmentSafetyBadgeDescriptor[],
): BadgeColor => {
  if (badges.some((badge) => badge.severity === "error")) return "error";
  if (badges.some((badge) => badge.severity === "warning")) return "warning";
  if (badges.some((badge) => badge.severity === "success")) return "success";
  if (badges.some((badge) => badge.severity === "info")) return "info";
  return "default";
};

export const getBadgeSummaryLabel = (
  collapsed: boolean,
  badgeCount: number,
): string => {
  if (!collapsed) {
    return "Environment safety";
  }

  return `${badgeCount} badge${badgeCount === 1 ? "" : "s"}`;
};
